import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Toolbar from '../../common/Toolbar';
import ProductFilter from '../../filters/ProductFilter';
import styles from './ProductsPermissions.module.scss';
import ActionLinkTop from '../../common/ActionLinkTop';
import ThemeFilterRetailer from '../../../components/theme/ThemeFilterRetailer';
import Search from '../../common/Search';
import ProductCard from '../../common/ProductCard';
import Button from '../../common/Button';
import ThemeRetailer from '../../../components/theme/ThemeRetailer';
import WrapperColumnRetailers from '../../../components/grid-container/WrapperColumnRetailers';
import CountTop from '../../../components/common/CountTop/CountTop';
import Wall from '../../common/Wall';
import { TAB_DROPSHIP, TAB_REORDER } from '../../../constants/mainConstants';
import ProductsListView from '../ProductsListView';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTh } from '@fortawesome/free-solid-svg-icons';
import { StaticFilters } from '../../../constants/staticFilters';
import { upperFirst } from 'lodash';
import { useSelector } from 'react-redux';
import { ActiveStatus } from '../../../constants/activeStatus';
import { BlockSelectAllModal } from '../../common/SelectAllModal';
import { MAX_ITEMS_TO_SELECT_ALL } from '../../common/SelectAllModal/consts';
import { getApprovedStatusForVariants } from '../../../utils/products-permissions';

const LISTVIEW = 'listview';
const GRIDVIEW = 'gridview';

function ProductsPermissions(props) {
  const {
    pageCount,
    count,
    selectedCount,
    productSearch,
    setFilter,
    setFilterSearch,
    searchQuery,
    perPage,
    offset,
    selectAllOnPage,
    unselectAll,
    autoSync = false,
    isLoadingProducts = false,
    shouldAllowUserToDoActions = false,
    checked = {},
  } = props;

  const permissionType = props.permissionType.toLowerCase();

  const [shouldOpenSelectAllModal, setShouldOpenSelectAllModal] = useState(false);
  const [view, setView] = useLocalStorage('ProductsPermissionsView', LISTVIEW);

  const onCheckProduct = (isProductChecked, value) => {
    const { check, uncheck } = props;

    if (isProductChecked) {
      check(value);
    } else {
      uncheck(value);
    }
  };

  const { currentFilters } = useSelector((state) => state.productFilters);

  const isFilteringArchived = useMemo(() => {
    return currentFilters?.activeProducts === ActiveStatus.ARCHIVED;
  }, [currentFilters]);

  const shouldDisableAction = Object.keys(checked).length === 0 || isFilteringArchived;

  const isChecked = (model) => {
    return checked.hasOwnProperty(model);
  };

  const handlePageClick = (data) => {
    const { perPage, setPage } = props;
    setPage(perPage * data.selected);
  };

  const products = () => {
    const { data, openModal } = props;

    if (!data) {
      return null;
    }

    if (view === LISTVIEW) {
      return (
        <ProductsListView
          products={data}
          order={props.order}
          orderBy={props.orderBy}
          setOrder={props.setOrder}
          open={openModal}
          permissionType={permissionType}
          getApprovedStatusForVariants={(row) =>
            getApprovedStatusForVariants({ variants: row.variants, permissionType })
          }
          isChecked={isChecked}
          onChecked={onCheckProduct}
          shouldAllowUserToDoActions={shouldAllowUserToDoActions}
        />
      );
    } else {
      return (
        <>
          {data.map((item) => (
            <ThemeRetailer key={item.model}>
              <ProductCard
                product={item}
                checkbox
                onCheck={onCheckProduct}
                checked={isChecked(item.model)}
                approvedStatus={getApprovedStatusForVariants({ variants: item.variants, permissionType })}
                open={openModal}
                shouldAllowUserToDoActions={shouldAllowUserToDoActions}
                isVerboseInventoryInfo
              />
            </ThemeRetailer>
          ))}
        </>
      );
    }
  };

  const actionLinks = () => (
    <>
      <Button disabled={shouldDisableAction} text="Approve" onClick={props.approveChecked} />
      <Button text="Revoke" color="white" disabled={shouldDisableAction} onClick={props.unapproveChecked} margin_left />
    </>
  );

  const toggleView = () => {
    return (
      <span
        className={cn(styles.toggleView, view === LISTVIEW ? 'ic-dashboard' : 'ic-menu')}
        title="Toggle View"
        onClick={() => setView(view === LISTVIEW ? GRIDVIEW : LISTVIEW)}
      >
        {view === LISTVIEW && <FontAwesomeIcon icon={faTh} />}
      </span>
    );
  };

  const staticFilters = [StaticFilters.HAS_INVENTORY, StaticFilters.ACTIVE_PRODUCTS];
  if (permissionType === TAB_DROPSHIP) {
    staticFilters.push(StaticFilters.APPROVED_STATUS);
  } else if (permissionType === TAB_REORDER) {
    staticFilters.push(StaticFilters.IS_APPROVED);
  }
  const handleSelectAll = () => {
    if (count > MAX_ITEMS_TO_SELECT_ALL) {
      setShouldOpenSelectAllModal(true);
    } else props.selectAll();
  };
  return (
    <>
      <div className={styles.wrapperToolbar}>
        <Toolbar
          name={`${permissionType === TAB_REORDER ? `${upperFirst(permissionType)} ` : ''}Catalogue Management`}
          grow
          width={660}
        >
          {shouldAllowUserToDoActions ? (
            <div className={styles.right}>
              <CountTop title="All" count={count} />
              <CountTop title="Selected" count={selectedCount} />
              <Wall />
              <ActionLinkTop title="Select this page" onClick={selectAllOnPage} />
              <ActionLinkTop title="Select all" onClick={() => handleSelectAll()} />
              <ActionLinkTop title="Unselect all" onClick={unselectAll} />
              <Wall />
              {actionLinks()}
            </div>
          ) : (
            <></>
          )}
          <Wall />
          {toggleView()}
        </Toolbar>

        <div className={styles.isHide}>
          <div className={cn('row', 'vertical-indent', view === 'listview' ? styles.listView : styles.gridView)}>
            <ThemeFilterRetailer>
              <Search searchQuery={searchQuery} search={productSearch} />

              <ProductFilter
                staticFilters={staticFilters}
                setFilter={setFilter}
                setFilterSearch={setFilterSearch}
                connectionHasAutoSync={autoSync}
              />
            </ThemeFilterRetailer>

            <WrapperColumnRetailers
              isLoadingProducts={isLoadingProducts}
              pageCount={pageCount}
              handlePageClick={handlePageClick}
              currentPage={offset / perPage}
            >
              {products()}
            </WrapperColumnRetailers>
          </div>
        </div>
      </div>
      <BlockSelectAllModal open={shouldOpenSelectAllModal} onClose={() => setShouldOpenSelectAllModal(false)} />
    </>
  );
}

export default ProductsPermissions;

ProductsPermissions.propTypes = {
  autoSync: PropTypes.bool,
  permissionType: PropTypes.string.isRequired,
  perPage: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  selectedCount: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  pageCount: PropTypes.number.isRequired,
  approveChecked: PropTypes.func.isRequired,
  unapproveChecked: PropTypes.func.isRequired,
  productSearch: PropTypes.func.isRequired,
  check: PropTypes.func,
  uncheck: PropTypes.func,
  checked: PropTypes.object,
  setPage: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  setFilterSearch: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  offset: PropTypes.number.isRequired,
  selectAll: PropTypes.func.isRequired,
  selectAllOnPage: PropTypes.func.isRequired,
  unselectAll: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  isLoadingProducts: PropTypes.bool,
  shouldAllowUserToDoActions: PropTypes.bool,
};
